/**
 * Jupiter, rendered in WebGL in two passes:
 *   1. once at startup, the cloud deck is generated into a large equirectangular texture (4096x2048 where the GPU
 *      allows it): Jupiter's belt/zone profile by latitude, domain-warped turbulence sheared along each band, the
 *      Great Red Spot with its swirl, a few white ovals. Mipmapped, so the sphere samples it smoothly at any size.
 *   2. every frame, that texture is wrapped on a sphere lit from behind (a thin crescent, the face toward us in
 *      shadow) with a soft terminator, limb darkening, a lit haze at the limb, an antialiased silhouette, a sparse
 *      starfield and Elara crossing in front. Filmic tone mapping and a little grain.
 * No image assets. Falls back to the CSS gradient on the canvas without WebGL.
 */
import { useEffect, useRef } from "react";

const QUAD_VERT = `
attribute vec2 p;
varying vec2 v;
void main() { v = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }
`;

// ── pass 1: the cloud deck (equirectangular) ──
const CLOUDS_FRAG = `
precision highp float;
varying vec2 v;

float h13(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
float n3(vec3 p) {
  vec3 i = floor(p), f = fract(p);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = h13(i), b = h13(i + vec3(1,0,0)), c = h13(i + vec3(0,1,0)), d = h13(i + vec3(1,1,0));
  float e = h13(i + vec3(0,0,1)), f1 = h13(i + vec3(1,0,1)), g = h13(i + vec3(0,1,1)), h = h13(i + vec3(1,1,1));
  return mix(mix(mix(a, b, u.x), mix(c, d, u.x), u.y), mix(mix(e, f1, u.x), mix(g, h, u.x), u.y), u.z);
}
float fbm(vec3 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 7; i++) { s += a * n3(p); p = p * 2.02 + vec3(13.1, 7.7, 3.3); a *= 0.5; }
  return s;
}

// Jupiter's zonal profile: pale zones and brown belts by latitude (radians).
vec3 zonal(float lat, out float zone) {
  float l = lat * 57.2958;
  zone = 0.0;
  zone += 1.0 * exp(-pow(l / 6.5, 2.0));             // Equatorial Zone
  zone -= 1.1 * exp(-pow((l - 12.0) / 5.0, 2.0));    // North Equatorial Belt
  zone -= 1.0 * exp(-pow((l + 13.0) / 5.5, 2.0));    // South Equatorial Belt
  zone += 0.8 * exp(-pow((l - 20.0) / 3.5, 2.0));    // North Tropical Zone
  zone += 0.9 * exp(-pow((l + 23.0) / 4.0, 2.0));    // South Tropical Zone
  zone -= 0.7 * exp(-pow((l - 27.0) / 3.0, 2.0));    // North Temperate Belt
  zone -= 0.6 * exp(-pow((l + 30.0) / 3.0, 2.0));    // South Temperate Belt
  zone += 0.5 * exp(-pow((l - 33.0) / 3.0, 2.0));
  zone += 0.4 * exp(-pow((l + 36.0) / 3.0, 2.0));
  zone -= 0.35 * sin(l * 0.55) * smoothstep(38.0, 55.0, abs(l)); // narrow high-latitude bands
  vec3 zoneCol = vec3(0.86, 0.80, 0.70);
  vec3 beltCol = vec3(0.50, 0.32, 0.20);
  vec3 c = mix(beltCol, zoneCol, smoothstep(-0.9, 0.9, zone));
  float polar = smoothstep(45.0, 75.0, abs(l));
  return mix(c, vec3(0.42, 0.40, 0.40), polar * 0.75);
}

void main() {
  float lon = v.x * 6.2831853;
  float lat = (v.y - 0.5) * 3.1415926;
  vec3 sp = vec3(cos(lat) * sin(lon), sin(lat), cos(lat) * cos(lon));

  // shear: each latitude drifts (zonal jets), so turbulence streaks along the bands
  float jet = sin(lat * 18.0) * 0.6 + sin(lat * 7.0) * 0.3;
  vec3 q = vec3(sp.x * cos(jet) - sp.z * sin(jet), sp.y, sp.x * sin(jet) + sp.z * cos(jet));
  // domain warp, stretched in longitude
  vec3 w = q * vec3(3.0, 11.0, 3.0);
  vec3 warp = vec3(fbm(w + 1.7), fbm(w + 9.2), fbm(w + 4.4));
  float turb = fbm(w * 1.6 + warp * 2.2);
  float fine = fbm(q * vec3(9.0, 40.0, 9.0) + warp * 1.4);

  float zone;
  float pertLat = lat + (turb - 0.5) * 0.085 + (fine - 0.5) * 0.02; // wavy band edges, festoons
  vec3 col = zonal(pertLat, zone);
  col *= 0.82 + 0.36 * turb;
  col = mix(col, col * vec3(1.08, 1.0, 0.92), smoothstep(0.55, 0.8, fine));
  float edge = 1.0 - abs(zone);
  col = mix(col, vec3(0.92, 0.88, 0.80), smoothstep(0.62, 0.78, fine) * edge * 0.6); // bright plumes
  col = mix(col, vec3(0.32, 0.18, 0.12), smoothstep(0.66, 0.82, turb) * edge * 0.5); // dark barges

  // Great Red Spot: 22 deg S, an oval with a swirl and a pale collar
  vec2 g = vec2((mod(lon + 3.1415926 - 4.2, 6.2831853) - 3.1415926) * cos(lat) / 0.22, (lat + 0.384) / 0.105);
  float gr = length(g);
  float ang = atan(g.y, g.x) + (1.0 - smoothstep(0.0, 1.2, gr)) * 3.0;
  float swirl = fbm(vec3(cos(ang) * gr * 3.0, sin(ang) * gr * 3.0, 2.0));
  float spot = smoothstep(1.0, 0.75, gr);
  float collar = smoothstep(1.35, 1.0, gr) - spot;
  col = mix(col, vec3(0.88, 0.80, 0.70), collar * 0.55);
  col = mix(col, mix(vec3(0.62, 0.26, 0.14), vec3(0.78, 0.40, 0.24), swirl), spot * 0.9);

  // white ovals in the southern temperate belt
  for (int k = 0; k < 3; k++) {
    float fk = float(k);
    vec2 o = vec2((mod(lon + 3.1415926 - 1.0 - fk * 0.55, 6.2831853) - 3.1415926) * cos(lat) / 0.05, (lat + 0.68) / 0.03);
    col = mix(col, vec3(0.93, 0.91, 0.86), smoothstep(1.0, 0.4, length(o)) * 0.85);
  }
  gl_FragColor = vec4(col, 1.0);
}
`;

// ── pass 2: the planet in space ──
const SCENE_FRAG = `
precision highp float;
varying vec2 v;
uniform sampler2D clouds;
uniform vec2 res;
uniform float t;
uniform float scroll;
uniform float px; // one device pixel in planet radii, for the antialiased limb

float h12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * res) / min(res.x, res.y);
  float aspect = res.x / res.y;
  vec2 c = vec2(max(0.42, 0.5 * aspect - 0.42), -0.20 - scroll * 0.35);
  float R = 0.76;
  vec2 d = (uv - c) / R;
  float r = length(d);
  vec3 L = normalize(vec3(-0.58, 0.70, -0.42)); // sun behind, upper left

  // stars: sparse, varied, faint, steady
  vec2 cell = floor(gl_FragCoord.xy / 3.0);
  float s = h12(cell);
  vec3 col = vec3(0.0);
  if (s > 0.9965) {
    vec2 sc = (cell + 0.5) * 3.0;
    float sd = length(gl_FragCoord.xy - sc);
    float mag = pow(h12(cell + 7.0), 3.0);
    col += mix(vec3(0.75, 0.82, 1.0), vec3(1.0, 0.9, 0.78), h12(cell + 3.0)) * smoothstep(1.6, 0.0, sd) * (0.15 + 0.85 * mag) * 0.7;
  }

  float cover = smoothstep(1.0 + px, 1.0 - px, r); // antialiased silhouette
  float zFront = sqrt(max(1.0 - r * r, 0.0)); // depth of Jupiter's visible surface here, in planet radii
  if (r < 1.0 + px) {
    float rr = min(r, 0.9999);
    vec3 n = vec3(d / max(r, 1e-4) * rr, sqrt(1.0 - rr * rr));
    float ca = cos(0.055), sa = sin(0.055); // slight axial tilt
    vec3 m = vec3(ca * n.x - sa * n.y, sa * n.x + ca * n.y, n.z);
    float lat = asin(clamp(m.y, -1.0, 1.0));
    float lon = atan(m.x, m.z);
    vec2 tc = vec2(lon / 6.2831853 + 0.5 + t * 0.0025, 0.5 + lat / 3.1415926);
    vec3 alb = texture2D(clouds, tc).rgb;

    float ndl = dot(n, L);
    float diff = smoothstep(-0.08, 0.55, ndl) * max(ndl + 0.08, 0.0) / 1.08; // soft terminator
    float limb = pow(max(n.z, 0.0), 0.28); // limb darkening
    vec3 surf = alb * diff * limb * 1.35;
    surf += alb * 0.012; // the night side: bands just perceptible
    float rim = pow(1.0 - max(n.z, 0.0), 3.0);
    float sunSide = max(dot(normalize(n.xy + 1e-5), normalize(L.xy)), 0.0);
    surf += vec3(1.0, 0.78, 0.55) * rim * pow(sunSide, 2.0) * 0.65 * smoothstep(-0.2, 0.3, ndl); // lit haze
    col = mix(col, surf, cover);
  }
  float halo = exp(-max(r - 1.0, 0.0) * 22.0) * (1.0 - cover);
  float side = max(dot(normalize(d), normalize(L.xy)), 0.0);
  col += vec3(1.0, 0.72, 0.48) * halo * pow(side, 3.0) * 0.28;

  // ── moons ──
  // Eight real moons, inner to outer: Metis, Adrastea, Amalthea, Thebe, then Io, Europa, Ganymede, Callisto. True
  // period ratios (Io laps every 160 s of wall-clock time, the rest scale from it), true order and colours; orbit radii compressed and
  // sizes enlarged so they read on screen. The orbital plane's tilt, position angle and the starting phases were
  // chosen numerically so a moon is on screen about 98% of the time on laptop and desktop shapes (no gap over ~4 s).
  // Lit a little more from the side than the planet so each shows a half phase; hidden behind Jupiter.
  float pxs = 1.0 / min(res.x, res.y);
  float tm = t * 0.25; // moons run at a quarter of the original pace so they drift rather than distract
  vec3 Lm = normalize(vec3(-0.62, 0.60, 0.12));
  float cpa = cos(-0.321), spa = sin(-0.321);
  for (int k = 0; k < 8; k++) {
    float period, orbit, size, phase0;
    vec3 tint;
    if (k == 0) { period = 0.295; orbit = 1.06; size = 0.0060; phase0 = 5.253; tint = vec3(0.52, 0.40, 0.34); }       // Metis
    else if (k == 1) { period = 0.298; orbit = 1.09; size = 0.0050; phase0 = 3.683; tint = vec3(0.50, 0.42, 0.36); }  // Adrastea
    else if (k == 2) { period = 0.498; orbit = 1.17; size = 0.0085; phase0 = 2.275; tint = vec3(0.62, 0.36, 0.26); }  // Amalthea
    else if (k == 3) { period = 0.675; orbit = 1.27; size = 0.0070; phase0 = 1.509; tint = vec3(0.55, 0.50, 0.46); }  // Thebe
    else if (k == 4) { period = 1.769; orbit = 1.42; size = 0.0175; phase0 = 0.663; tint = vec3(0.95, 0.85, 0.50); }  // Io
    else if (k == 5) { period = 3.551; orbit = 1.66; size = 0.0155; phase0 = 0.218; tint = vec3(0.93, 0.90, 0.84); }  // Europa
    else if (k == 6) { period = 7.155; orbit = 1.96; size = 0.0250; phase0 = 2.703; tint = vec3(0.70, 0.64, 0.56); }  // Ganymede
    else { period = 16.689; orbit = 2.34; size = 0.0230; phase0 = 0.760; tint = vec3(0.48, 0.43, 0.38); }             // Callisto
    float a = 1.0 + (orbit - 1.0) * 0.682;
    float th = phase0 + tm * 6.2831853 / (40.0 * period / 1.769);
    float z = sin(th) * a;
    vec2 q0 = vec2(cos(th) * a, z * -0.267);
    vec2 q = vec2(q0.x * cpa - q0.y * spa, q0.x * spa + q0.y * cpa);
    vec2 dm = (uv - (c + q * R)) / size;
    float dr = length(dm);
    float aa = pxs / size * 1.2;
    float disc = smoothstep(1.0 + aa, 1.0 - aa, dr);
    if (disc > 0.0) {
      vec3 nm = vec3(dm, sqrt(max(1.0 - dr * dr, 0.0)));
      // per-pixel depth test against the planet's surface: the limb slides over the moon instead of popping it
      float zPix = z + (size / R) * nm.z;
      float occluded = cover * smoothstep(0.004, -0.004, zPix - zFront);
      disc *= 1.0 - occluded;
      float ndl = dot(nm, Lm);
      float lit = smoothstep(-0.05, 0.3, ndl) * max(ndl, 0.0);
      float rimM = pow(1.0 - nm.z, 2.5) * max(dot(normalize(dm + 1e-5), normalize(Lm.xy)), 0.0);
      vec3 moon = tint * (lit * 1.45 + 0.03) + tint * rimM * 0.3;
      col = mix(col, moon, disc);
    }
  }
  // the small outer moons (Himalia group, Ananke, Carme, Pasiphae...): faint points on wide, slow, inclined orbits
  for (int k = 0; k < 6; k++) {
    float fk = float(k);
    float th = fk * 1.7 + tm * (0.012 + fk * 0.004) * (mod(fk, 2.0) * 2.0 - 1.0); // some retrograde
    vec2 o = vec2(cos(th) * (2.9 + fk * 0.35), sin(th) * (0.22 + fk * 0.07) - 0.05);
    vec2 sp2 = c + o * R;
    float spBehind = smoothstep(0.05, -0.05, sin(th)) * cover; // behind the planet: hidden where it covers
    col += vec3(0.95, 0.92, 0.88) * smoothstep(1.6 * pxs, 0.0, length(uv - sp2)) * (0.35 + 0.08 * fk) * (1.0 - spBehind);
  }
  // Elara: the brightest of the outer moons, crossing in front
  float a = tm * 0.03;
  vec2 e = c + vec2(cos(a) * 1.5, sin(a) * 0.20 - 0.08) * R;
  float eBehind = smoothstep(0.05, -0.05, sin(a)) * cover;
  col += vec3(1.0, 0.95, 0.88) * smoothstep(2.2 * pxs, 0.0, length(uv - e)) * 0.9 * (1.0 - eBehind);

  col = aces(col * 1.15);
  float vig = smoothstep(1.45, 0.3, length(uv * vec2(0.8, 1.0)));
  col *= mix(0.55, 1.0, vig);
  col += (h12(gl_FragCoord.xy + fract(t) * 100.0) - 0.5) * 0.008;
  gl_FragColor = vec4(col, 1.0);
}
`;

function compile(gl: WebGLRenderingContext, vs: string, fs: string): WebGLProgram | null {
  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error("Jupiter shader:", gl.getShaderInfoLog(s));
    return s;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, sh(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error("Jupiter program:", gl.getProgramInfoLog(p));
    return null;
  }
  return p;
}

// Scene time at load: a frame with several moons in view (also the still frame shown when motion is off).
const START_T = 792.16;

export function JupiterCanvas({ animate }: { animate: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef<((tSeconds: number) => void) | null>(null);
  const clock = useRef(START_T);
  useEffect(() => {
    const canvas = ref.current!;
    const gl = canvas.getContext("webgl", { antialias: true, alpha: false });
    if (!gl) return;
    const cloudsProg = compile(gl, QUAD_VERT, CLOUDS_FRAG);
    const sceneProg = compile(gl, QUAD_VERT, SCENE_FRAG);
    if (!cloudsProg || !sceneProg) return;

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const bindQuad = (prog: WebGLProgram) => {
      const loc = gl.getAttribLocation(prog, "p");
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    };

    // pass 1, once: bake the cloud deck
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    const texW = maxTex >= 4096 && window.innerWidth > 900 ? 4096 : 2048;
    const texH = texW / 2;
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, texW, texH, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.viewport(0, 0, texW, texH);
    gl.useProgram(cloudsProg);
    bindQuad(cloudsProg);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    const aniso = gl.getExtension("EXT_texture_filter_anisotropic");
    if (aniso) gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));

    // pass 2, every frame
    gl.useProgram(sceneProg);
    bindQuad(sceneProg);
    const u = (n: string) => gl.getUniformLocation(sceneProg, n);
    const uRes = u("res"), uT = u("t"), uScroll = u("scroll"), uPx = u("px");
    gl.uniform1i(u("clouds"), 0);
    // ?t=<seconds> freezes the scene at that moment (visual QA of moon transits and occultations)
    const frozen = Number(new URLSearchParams(window.location.search).get("t"));
    drawRef.current = (tSeconds: number) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.floor(canvas.clientWidth * dpr);
      const h = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uRes, w, h);
      gl.uniform1f(uPx, 1.5 / (0.76 * Math.min(w, h)));
      gl.uniform1f(uT, frozen > 0 ? frozen : tSeconds);
      gl.uniform1f(uScroll, Math.min(1, window.scrollY / Math.max(1, window.innerHeight * 4)));
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    drawRef.current(clock.current);
    return () => {
      drawRef.current = null;
      gl.deleteTexture(tex);
      gl.deleteFramebuffer(fb);
    };
  }, []);

  // Motion: driven by the wall clock, not by frames, so 60 Hz, 120 Hz and throttled screens all move at the same
  // speed. Paused, the scene holds its frame and only redraws on resize/scroll.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const still = () => drawRef.current?.(clock.current);
    if (animate) {
      const tick = (now: number) => {
        clock.current += Math.min(now - last, 250) / 1000; // a backgrounded tab resumes where it left off
        last = now;
        drawRef.current?.(clock.current);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame((now) => {
        last = now;
        tick(now);
      });
    } else {
      still();
    }
    window.addEventListener("resize", still);
    window.addEventListener("scroll", still, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", still);
      window.removeEventListener("scroll", still);
    };
  }, [animate]);
  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", display: "block", background: "radial-gradient(ellipse at 70% 80%, #1a120c 0%, #000 60%)" }}
    />
  );
}
